// MyWaitingPosts.js
import React, {useContext, useEffect, useState} from 'react';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faUser } from "@fortawesome/free-regular-svg-icons";
import Zoom from "react-medium-image-zoom";
import LoadingSpinner from "../components/LoadingSpinner";
import 'react-medium-image-zoom/dist/styles.css';
import i18n from "../i18n";
import moment from "moment-timezone";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import DOMPurify from "dompurify";

function MyWaitingPosts() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const { currentUserId } = useContext(AuthContext);

    useEffect(() => {
        const fetchPosts = async () => {
            setLoading(true);
            const accessToken = localStorage.getItem('access_token');

            if (!accessToken) {
                setError('Unauthorized');
                setLoading(false);
                return;
            }

            try {
                const postsResponse = await api.get(`${process.env.REACT_APP_API_URL}/api/posts/approval/${currentUserId}`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });

                setPosts(postsResponse.data);
                setLoading(false);
            } catch (err) {
                setError('Error fetching waiting posts');
                setLoading(false);
            }
        };

        fetchPosts();
    }, [currentUserId]);

    const formatDate = (dateString) => {
        if (!dateString) {
            return t('admin.unknown');
        }
        let lang = i18n.language;
        const languageMapping = {
            'ua': 'uk',
        };
        const intlLanguage = languageMapping[lang] || lang;

        moment.locale(intlLanguage);
        const date = moment(dateString);
        return date.format('LLL');
    };

    const renderMedia = (media_url) => {
        if (!media_url) return null;

        // YouTube
        if (media_url.includes('youtube.com') || media_url.includes('youtu.be')) {
            let embedUrl = '';

            if (media_url.includes('/playlist?list=')) {
                const urlParams = new URLSearchParams(media_url.split('?')[1]);
                const playlistId = urlParams.get('list');
                if (playlistId) {
                    embedUrl = `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
                }
            } else {
                let videoId = '';

                if (media_url.includes('/shorts/')) {
                    const path = new URL(media_url).pathname;
                    const parts = path.split('/');
                    const shortsIndex = parts.indexOf('shorts');
                    if (shortsIndex !== -1 && shortsIndex + 1 < parts.length) {
                        const videoIdWithQuery = parts[shortsIndex + 1];
                        videoId = videoIdWithQuery.split('?')[0];
                    }
                } else if (media_url.includes('youtu.be')) {
                    videoId = media_url.split('youtu.be/')[1].split('?')[0];
                } else {
                    const urlParams = new URLSearchParams(media_url.split('?')[1]);
                    videoId = urlParams.get('v');
                }

                if (videoId) {
                    embedUrl = `https://www.youtube.com/embed/${videoId}`;
                }
            }

            if (embedUrl) {
                return (
                    <iframe
                        width="560"
                        height="315"
                        src={embedUrl}
                        title={media_url.includes('/playlist?list=') ? "YouTube playlist" : "YouTube video"}
                        frameBorder="0"
                        allowFullScreen
                    ></iframe>
                );
            } else {
                return <p>Error while loading video</p>;
            }
        }

        // Twitch clip
        if (media_url.includes('twitch.tv') && media_url.includes('/clip/')) {
            const parentDomain = 'superpeople.live';
            const clipId = media_url.split('/clip/')[1].split('?')[0];
            const embedUrl = `https://clips.twitch.tv/embed?clip=${clipId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch clip ${clipId}`}
                ></iframe>
            );
        }
        // Twitch video
        else if (media_url.includes('twitch.tv/videos/')) {
            const parentDomain = 'superpeople.live';
            const videoId = media_url.split('videos/')[1].split('?')[0];
            const embedUrl = `https://player.twitch.tv/?video=${videoId}&parent=${parentDomain}&autoplay=false`;

            return (
                <iframe
                    src={embedUrl}
                    height="480"
                    width="720"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen={true}
                    title={`Twitch video ${videoId}`}
                ></iframe>
            );
        }

        // Image
        if (media_url.match(/\.(jpeg|jpg|gif|png)$/i)) {
            return (
                <Zoom>
                    <img
                        src={media_url}
                        alt={t('userProfile.userMedia')}
                        style={{ cursor: 'zoom-in' }}
                    />
                </Zoom>
            );
        }

        // Video
        if (media_url.match(/\.(mp4|webm|ogg)$/i)) {
            return (
                <video controls width="600">
                    <source src={media_url} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        }

        // Default
        return (
            <a href={media_url} target="_blank" rel="noopener noreferrer">
                {media_url}
            </a>
        );
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <div>{error}</div>;

    return (
        <div className="my-waiting-posts-page posts-page">
            <div className="post-header">
                <h1>{t('posts.myWaitingPosts')}</h1>
                <img src="images/sp-line.png" alt={t('posts.loadingError')} id="line" />
            </div>

            {posts.length === 0 ? (
                <p>{t('posts.noWaitingPosts')}</p>
            ) : (
                <div className="posts-list">
                    <div className="section-title">
                        <span className="title-text">{t('posts.waitingPosts')}</span>
                        <span className="arrow-icon">›</span>
                    </div>
                    <div className="main-posts-wrapper">
                        {posts.map((post, index) => (
                            <div key={post.id} className={`post-card ${index === 0 ? 'no-top-border' : ''}`}>
                                <div className="post-content">
                                    <div className="post-creator">
                                        <FontAwesomeIcon icon={faUser} />
                                        <Link to={`/user/${post.user_id}`} className="post-username">
                                            {post.username}
                                        </Link>
                                    </div>
                                    <div className="post-info"
                                         dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(post.content)}}
                                    ></div>
                                    <div className="post-media">
                                        {renderMedia(post.media_url)}
                                    </div>
                                    <div className="post-date">
                                        <FontAwesomeIcon icon={faCalendarAlt} /> {formatDate(post.created_at)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default MyWaitingPosts;
